import React, { useState, useRef, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Button, FormControl, FormControlLabel, Input, Radio, RadioGroup } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import { useAuthStore } from "@user/store/auth_store";

import "./popup_sku_add.scoped.scss";

interface State {
  skuWbCellQtyCodes: Code[],
  skuWpCellQtyCodes: Code[]
}

const defState = {
  skuWbCellQtyCodes: [],
  skuWpCellQtyCodes: []
};

interface Info {
  skuNoEx: string,
  skuType: string,
  skuSize: string,
  skuCellQty: string,
  workStationId: string
}

const defInfo = {
  skuNoEx: "",
  skuType: "",
  skuSize: "",
  skuCellQty: "",
  workStationId: ""
};

const validationMsg = [
  {id: "skuNoEx", type: "NotBlank", msg: "SKU No를 입력해 주세요."},
  {id: "skuNoEx", type: "Size", msg: "SKU No를 확인해 주세요."},
  {id: "skuNoEx", type: "Duplicate", msg: "이미 등록된 SKU No입니다."},
  {id: "skuType", type: "NotBlank", msg: "SKU Type을 선택해 주세요."},
  {id: "skuSize", type: "NotBlank", msg: "SKU Size를 선택해 주세요."},
  {id: "skuCellQty", type: "NotNull", msg: "SKU Cell을 선택해 주세요."},
  {id: "skuCellQty", type: "Min", msg: "SKU CELL은 1이상으로 선택해 주세요."},
  {id: "workStationId", type: "NotBlank", msg: "작업대를 확인해 주세요."},
  {id: "locationId", type: "InvalidStatus", msg: "빈 로케이션이 없어 전입고를 진행할 수 없습니다."}
];

function PopupSkuAdd(props: any) {
  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    try {
      const data = await kutil.sendGet("/api/inoutbound/workstation_manage.data?mode=searchSkuCellQtyCodes", {}, {
        reqFunction: init
      });
      setState(prev => ({ ...prev, skuWbCellQtyCodes: data.skuWbCellQtyCodes, skuWpCellQtyCodes: data.skuWpCellQtyCodes }));
      return true;
    } catch(e) {
      console.log(e);
      return false;
    }
  }

  async function initParams() {
    info.skuType = (commonCode.skuType as any[]).filter(c => c.code === props.data.workStationInfo.workStationType)?.[0]?.code ?? "";
    
    setInfo(prev => ({ ...prev, skuType: info.skuType, workStationId: props.data.workStationInfo.workStationId }));
  }

  async function searchInfo() {
  }

  async function closePopup() {
    props.onClose();
  }

  async function save() {
    const isValid = await kutil.checkValidation(info, validationMsg);
    if (isValid == false) {
      return;
    }

    if (await kutil.confirm("추가하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=addSku", info, {
        validationMsg: validationMsg
      });
      await kutil.alert("추가하였습니다.");
      props.onClose({isSuccess: true});
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{"& .MuiDialog-container": {"& .MuiPaper-root": {width: "100%", maxWidth: "500px",},},}}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          SKU 추가
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* SKU 정보 */}
            <div>
              {/* 항목 */}
              <div>
                <p>SKU No</p>
                <div className="sg-item">
                  <Input
                    inputProps={{ maxLength: 100 }}
                    value={info.skuNoEx}
                    onChange={(e) => {setInfo(prev => ({ ...prev, skuNoEx: e.target.value }))}}
                    onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                  />
                </div>
              </div>
              <div>
                <p>SKU Type</p>
                <FormControl disabled>
                  <RadioGroup
                    value={info.skuType}
                  >
                    {(commonCode.skuType as any[]).map((code) => (
                      <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
              <div>
                <p>SKU Size</p>
                <FormControl>
                  <RadioGroup
                    value={info.skuSize}
                    onChange={(e) => {setInfo(prev => ({ ...prev, skuSize: e.target.value }))}}
                  >
                    {(commonCode.skuSize as any[]).map((code) => (
                      <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value}
                        sx={{ display: (
                          code.code == "SM"
                          || code.code == "MM"
                          || ((info.skuType == "WB" && code.code == "SS"))
                          || ((info.skuType == "WP" && code.code == "MB"))
                          ) ? "flex" : "none" }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
              <div>
                <p>SKU Cell</p>
                <FormControl>
                  <RadioGroup
                    value={info.skuCellQty}
                    onChange={(e) => {setInfo(prev => ({ ...prev, skuCellQty: e.target.value }))}}
                  >
                    {((info.skuType == "WB" ? state.skuWbCellQtyCodes : info.skuType == "WP" ? state.skuWpCellQtyCodes : []) as any[] ).map((code) => (
                      <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
            </div>
            {/*// SKU 정보 */}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            취소
          </Button>
          { auth.write &&
          <Button variant="contained" autoFocus onClick={save}>
            추가
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupSkuAdd);
